import { sendMessage } from './chatService';
import { emailNewMessage, emailNewProject, emailBudgetApproved, emailFileUploaded } from './emailService';

/**
 * Notification Service
 * Sends system chat notifications + email alerts to admin
 */

const sendSystemNotification = async (chatId, message) => {
  try {
    await sendMessage(
      chatId,
      'system',
      'System',
      'system',
      message,
      null
    );
    return { success: true };
  } catch (error) {
    console.error('Error sending notification:', error);
    return { success: false, error: error.message };
  }
};

/** Project Notifications */

export const notifyProjectCreated = async (clientId, projectName, adminName) => {
  const message = `🎉 ${adminName} created a new project: "${projectName}"`;
  return sendSystemNotification(clientId, message);
};

export const notifyProjectCompleted = async (clientId, projectName, adminName) => {
  const message = `✅ ${adminName} marked the project "${projectName}" as completed`;
  return sendSystemNotification(clientId, message);
};

export const notifyProjectReopened = async (clientId, projectName, adminName) => {
  const message = `🔄 ${adminName} reopened the project "${projectName}"`;
  return sendSystemNotification(clientId, message);
};

export const notifyBudgetRequestSubmitted = async (clientId, projectName, clientName) => {
  const message = `💬 ${clientName} submitted a budget request: "${projectName}"`;
  return sendSystemNotification(clientId, message);
};

/** Called when CLIENT creates a project */
export const notifyClientProjectCreated = async (clientName, projectName) => {
  emailNewProject(clientName, projectName).catch(console.error);
};

/** Called when CLIENT sends a chat message */
export const notifyClientMessage = async (clientName, messageContent, projectName) => {
  emailNewMessage(clientName, messageContent, projectName).catch(console.error);
};

/** Called when CLIENT approves a budget */
export const notifyClientBudgetApproved = async (clientName, projectName, total) => {
  emailBudgetApproved(clientName, projectName, total).catch(console.error);
};

/** Called when CLIENT uploads a file */
export const notifyClientFileUploaded = async (clientName, fileName, projectName, category) => {
  emailFileUploaded(clientName, fileName, projectName, category).catch(console.error);
};

/** Milestone Notifications */

export const notifyMilestoneCreated = async (clientId, milestoneName, projectName, creatorName) => {
  const message = `📌 ${creatorName} created a new milestone: "${milestoneName}" in project "${projectName}"`;
  return sendSystemNotification(clientId, message);
};

export const notifyMilestoneStatusChanged = async (clientId, milestoneName, oldStatus, newStatus, changerName) => {
  const statusLabels = { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' };
  const statusEmojis = { pending: '⏳', in_progress: '🔄', completed: '✅' };
  const emoji = statusEmojis[newStatus] || '🔔';
  const message = `${emoji} ${changerName} changed milestone "${milestoneName}" status from ${statusLabels[oldStatus] || oldStatus} to ${statusLabels[newStatus] || newStatus}`;
  return sendSystemNotification(clientId, message);
};

export const notifyMilestoneUpdated = async (clientId, milestoneName, updaterName) => {
  const message = `📝 ${updaterName} updated milestone "${milestoneName}"`;
  return sendSystemNotification(clientId, message);
};

export const notifyMilestoneDeleted = async (clientId, milestoneName, deleterName) => {
  const message = `🗑️ ${deleterName} deleted milestone "${milestoneName}"`;
  return sendSystemNotification(clientId, message);
};

export const notifyMilestoneReopened = async (clientId, milestoneName, reopenerName) => {
  const message = `🔄 ${reopenerName} reopened milestone "${milestoneName}"`;
  return sendSystemNotification(clientId, message);
};

/** File Notifications */

export const notifyFileUploaded = async (clientId, fileName, uploaderName, category) => {
  const categoryLabels = { draft: 'Draft', final: 'Final', client: 'Client Files' };
  const message = `📎 ${uploaderName} uploaded "${fileName}" to ${categoryLabels[category] || category}`;
  return sendSystemNotification(clientId, message);
};

export const notifyFileDeleted = async (clientId, fileName, deleterName) => {
  const message = `🗑️ ${deleterName} deleted file "${fileName}"`;
  return sendSystemNotification(clientId, message);
};
